import { configureStore, createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import { logger } from '@core/utils';
import { adoptTenantContext, TENANT_CHANGED_EVENT, TENANT_STORAGE_KEY } from '@core/api/tenantContext';
import { aiReducer, aiApi } from '@features/ai';
import i18n from '@core/i18n';
import { authReducer, authApi, initializeAuth, setCredentials, restoreSession, updateCurrentUser, markSessionAnonymous, logout } from '@features/auth';
import { dataroomApi } from '@features/dataroom';
import { projectsReducer, projectsApi, setCurrentProject, clearCurrentProject } from '@features/projects';
import { notificationsApi, llmConfigApi } from '@features/settings';
import { workspaceReducer, closeCanvas, closePreview, clearChatQuote } from '@features/workspace';

/**
 * Listener middleware for handling side effects
 */
const listenerMiddleware = createListenerMiddleware();

// Reset tenant-bound state whenever a new identity signs in.
listenerMiddleware.startListening({
  matcher: isAnyOf(setCredentials, logout, markSessionAnonymous),
  effect: (action, listenerApi) => {
    adoptTenantContext(action.payload?.user);
    if (setCredentials.match(action) || logout.match(action)) {
      localStorage.setItem(TENANT_STORAGE_KEY, JSON.stringify({ at: Date.now(), nonce: Math.random() }));
    }
    listenerApi.dispatch(authApi.util.resetApiState());
    localStorage.removeItem('currentProject');
    // Magic-link login may replace an existing identity without a logout.
    // Purge every tenant-bound selection and cached API response.
    listenerApi.dispatch(clearCurrentProject());
    // Close any open document surfaces: a document from the previous tenant
    // left open would be re-fetched immediately and 403 in the new one.
    listenerApi.dispatch(closeCanvas());
    listenerApi.dispatch(closePreview());
    listenerApi.dispatch(clearChatQuote());
    listenerApi.dispatch(aiApi.util.resetApiState());
    listenerApi.dispatch(projectsApi.util.resetApiState());
    listenerApi.dispatch(dataroomApi.util.resetApiState());
    listenerApi.dispatch(notificationsApi.util.resetApiState());
    listenerApi.dispatch(llmConfigApi.util.resetApiState());
  },
});

listenerMiddleware.startListening({
  actionCreator: restoreSession,
  effect: (action) => adoptTenantContext(action.payload.user),
});

// The signed-in user's saved language governs the interface; the browser
// detector only decides before sign-in.
listenerMiddleware.startListening({
  matcher: isAnyOf(setCredentials, restoreSession, updateCurrentUser),
  effect: (action) => {
    const user = updateCurrentUser.match(action) ? action.payload : action.payload.user;
    const { language } = user;
    if (language !== i18n.resolvedLanguage) i18n.changeLanguage(language);
  },
});

// Clear tenant-bound browser state on logout.
listenerMiddleware.startListening({
  actionCreator: logout,
  effect: () => {
    try {
      localStorage.removeItem('currentProject');
    } catch (error) {
      logger.error('Error clearing auth from localStorage:', error);
    }
  },
});

// Persist current project to localStorage
listenerMiddleware.startListening({
  actionCreator: setCurrentProject,
  effect: (action, listenerApi) => {
    try {
      const user = listenerApi.getState().auth.user;
      const userUuid = user?.uuid;
      if (!userUuid) {
        localStorage.removeItem('currentProject');
        return;
      }
      localStorage.setItem('currentProject', JSON.stringify({ userUuid, organizationUuid: user.active_organization, project: action.payload }));
    } catch (error) {
      logger.error('Error saving project to localStorage:', error);
    }
  },
});

// Clear project from localStorage
listenerMiddleware.startListening({
  actionCreator: clearCurrentProject,
  effect: () => {
    try {
      localStorage.removeItem('currentProject');
    } catch (error) {
      logger.error('Error clearing project from localStorage:', error);
    }
  },
});

/**
 * Redux store configuration
 */
export const store = configureStore({
  reducer: {
    auth: authReducer,
    [authApi.reducerPath]: authApi.reducer,
    ai: (state, action) => aiReducer(isAnyOf(setCredentials, logout, markSessionAnonymous)(action) ? undefined : state, action),
    [aiApi.reducerPath]: aiApi.reducer,
    projects: projectsReducer,
    [projectsApi.reducerPath]: projectsApi.reducer,
    [dataroomApi.reducerPath]: dataroomApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    [llmConfigApi.reducerPath]: llmConfigApi.reducer,
    workspace: (state, action) => workspaceReducer(isAnyOf(setCredentials, logout, markSessionAnonymous)(action) ? undefined : state, action),
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(listenerMiddleware.middleware)
      .concat(authApi.middleware)
      .concat(aiApi.middleware)
      .concat(projectsApi.middleware)
      .concat(dataroomApi.middleware)
      .concat(notificationsApi.middleware)
      .concat(llmConfigApi.middleware),
  devTools: import.meta.env.DEV,
});

// Initialize state from localStorage on app start
store.dispatch(initializeAuth());

function refreshTenantSession() {
  store.dispatch(markSessionAnonymous());
  globalThis.location.reload();
}

globalThis.addEventListener(TENANT_CHANGED_EVENT, refreshTenantSession);
globalThis.addEventListener('storage', (event) => {
  if (event.key === TENANT_STORAGE_KEY) refreshTenantSession();
});
